import { isAiEffort, isAiTaskId, isValidAiModelId, MAX_AI_SYSTEM_PROMPT_LENGTH, } from './aiTasks.js';
import { isSettingsAiProvider, isSlackReplyTone } from './settings.js';
import { normalizeGmailAttentionRules } from './gmail.js';
import { normalizeCompetenceProfile } from './competence.js';
export function isRecord(u) {
    return typeof u === 'object' && u !== null && !Array.isArray(u);
}
export function isString(u) {
    return typeof u === 'string';
}
export function isNumber(u) {
    return typeof u === 'number' && Number.isFinite(u);
}
export function isBoolean(u) {
    return typeof u === 'boolean';
}
export function isArray(u) {
    return Array.isArray(u);
}
export function asOptionalString(u) {
    return typeof u === 'string' ? u : undefined;
}
export function asOptionalNumber(u) {
    return typeof u === 'number' && Number.isFinite(u) ? u : undefined;
}
export function asOptionalBoolean(u) {
    return typeof u === 'boolean' ? u : undefined;
}
export function parseApiEnvelope(raw, parseData) {
    if (!isRecord(raw)) {
        return { ok: false, reason: 'Envelope must be an object' };
    }
    if (!('data' in raw)) {
        return { ok: false, reason: 'Missing data field in envelope' };
    }
    if (!isString(raw.serverTime)) {
        return { ok: false, reason: 'Missing or non-string serverTime field in envelope' };
    }
    let data = raw.data;
    if (parseData) {
        const validated = parseData(raw.data);
        if (validated === null) {
            return { ok: false, reason: 'Invalid data payload schema' };
        }
        data = validated;
    }
    return {
        ok: true,
        value: {
            data,
            serverTime: raw.serverTime,
            ...(raw.pageInfo && isRecord(raw.pageInfo) ? { pageInfo: raw.pageInfo } : {}),
            ...(raw.rateLimit && isRecord(raw.rateLimit) ? { rateLimit: raw.rateLimit } : {}),
            ...(raw.cache && isRecord(raw.cache) ? { cache: raw.cache } : {}),
        },
    };
}
export function parseApiErrorBody(raw) {
    if (!isRecord(raw) || !isRecord(raw.error)) {
        return { ok: false, reason: 'Expected object with error field' };
    }
    const err = raw.error;
    if (!isString(err.code) || !isString(err.message) || !isNumber(err.status)) {
        return { ok: false, reason: 'Error object must contain code, message, and status' };
    }
    return {
        ok: true,
        value: {
            error: {
                code: err.code,
                message: err.message,
                status: err.status,
                ...(err.details && isRecord(err.details) ? { details: err.details } : {}),
                ...(typeof err.retryAfter === 'number' ? { retryAfter: err.retryAfter } : {}),
            },
        },
    };
}
/** Parse the `ai` sub-object of a settings update, or `undefined` when absent. */
function parseAiUpdate(raw) {
    if (!('ai' in raw) || raw.ai === undefined)
        return { ok: true, value: undefined };
    if (!isRecord(raw.ai))
        return { ok: false, reason: 'ai settings must be an object' };
    const ai = {};
    if (raw.ai.provider !== undefined) {
        if (!isSettingsAiProvider(raw.ai.provider)) {
            return { ok: false, reason: 'ai.provider must be a known AI provider' };
        }
        ai.provider = raw.ai.provider;
    }
    if (raw.ai.model !== undefined) {
        // Same rule as the per-task model: accepted-then-coerced is how a caller
        // gets 200 OK for a route it never chose.
        if (!isValidAiModelId(raw.ai.model)) {
            return { ok: false, reason: 'ai.model is not a valid model id' };
        }
        ai.model = raw.ai.model;
    }
    if (raw.ai.fallbackProvider !== undefined) {
        if (raw.ai.fallbackProvider !== 'none' && !isSettingsAiProvider(raw.ai.fallbackProvider)) {
            return { ok: false, reason: "ai.fallbackProvider must be a known AI provider or 'none'" };
        }
        ai.fallbackProvider = raw.ai.fallbackProvider;
    }
    if (raw.ai.fallbackModel !== undefined) {
        if (raw.ai.fallbackModel !== '' && !isValidAiModelId(raw.ai.fallbackModel)) {
            return { ok: false, reason: 'ai.fallbackModel is not a valid model id' };
        }
        ai.fallbackModel = raw.ai.fallbackModel;
    }
    if (raw.ai.effort !== undefined) {
        // `null` is the explicit "back to each task's default" signal; omitting the
        // field can only mean "leave it alone" (see UserSettingsUpdate).
        if (raw.ai.effort !== null && !isAiEffort(raw.ai.effort)) {
            return { ok: false, reason: 'ai.effort must be an AI effort or null' };
        }
        ai.effort = raw.ai.effort;
    }
    if (raw.ai.tasks !== undefined) {
        const tasks = parseAiTasksUpdate(raw.ai.tasks);
        if (!tasks.ok)
            return tasks;
        ai.tasks = tasks.value;
    }
    return { ok: true, value: ai };
}
const AI_TASK_STRING_FIELDS = ['model', 'fallbackModel', 'systemPrompt'];
/**
 * Bound a single task override. A rejected field fails the whole write rather
 * than being silently dropped: a Settings save that reports success while
 * discarding the prompt the user just wrote is worse than an error.
 */
function parseAiTaskOverride(task, raw) {
    if (!isRecord(raw))
        return { ok: false, reason: `ai.tasks.${task} must be an object` };
    const override = {};
    for (const field of AI_TASK_STRING_FIELDS) {
        if (raw[field] === undefined)
            continue;
        if (!isString(raw[field])) {
            return { ok: false, reason: `ai.tasks.${task}.${field} must be a string` };
        }
        override[field] = raw[field];
    }
    if (override.systemPrompt !== undefined &&
        override.systemPrompt.length > MAX_AI_SYSTEM_PROMPT_LENGTH) {
        return {
            ok: false,
            reason: `ai.tasks.${task}.systemPrompt must be at most ${MAX_AI_SYSTEM_PROMPT_LENGTH} characters`,
        };
    }
    for (const field of ['model', 'fallbackModel']) {
        const value = override[field];
        if (value !== undefined && value !== '' && !isValidAiModelId(value)) {
            return { ok: false, reason: `ai.tasks.${task}.${field} is not a valid model id` };
        }
    }
    if (raw.effort !== undefined) {
        if (!isAiEffort(raw.effort)) {
            return { ok: false, reason: `ai.tasks.${task}.effort must be an AI effort` };
        }
        override.effort = raw.effort;
    }
    if (raw.provider !== undefined) {
        if (!isSettingsAiProvider(raw.provider)) {
            return { ok: false, reason: `ai.tasks.${task}.provider must be a known AI provider` };
        }
        override.provider = raw.provider;
    }
    if (raw.fallbackProvider !== undefined) {
        if (raw.fallbackProvider !== 'none' && !isSettingsAiProvider(raw.fallbackProvider)) {
            return {
                ok: false,
                reason: `ai.tasks.${task}.fallbackProvider must be a known AI provider or 'none'`,
            };
        }
        override.fallbackProvider = raw.fallbackProvider;
    }
    if (raw.tone !== undefined) {
        if (!isSlackReplyTone(raw.tone)) {
            return { ok: false, reason: `ai.tasks.${task}.tone must be a known tone` };
        }
        override.tone = raw.tone;
    }
    return { ok: true, value: override };
}
function parseAiTasksUpdate(raw) {
    if (!isRecord(raw))
        return { ok: false, reason: 'ai.tasks must be an object' };
    const tasks = {};
    for (const [key, value] of Object.entries(raw)) {
        if (!isAiTaskId(key))
            return { ok: false, reason: `ai.tasks.${key} is not a known AI task` };
        const parsed = parseAiTaskOverride(key, value);
        if (!parsed.ok)
            return parsed;
        tasks[key] = parsed.value;
    }
    return { ok: true, value: tasks };
}
/** Parse the `slackVoice` sub-object of a settings update, or `undefined` when absent. */
function parseSlackVoiceUpdate(raw) {
    if (!('slackVoice' in raw) || raw.slackVoice === undefined)
        return { ok: true, value: undefined };
    if (!isRecord(raw.slackVoice))
        return { ok: false, reason: 'slackVoice settings must be an object' };
    const slackVoice = {};
    const src = raw.slackVoice;
    const stringFields = ['language', 'tone', 'replyLength', 'customPrompt'];
    for (const field of stringFields) {
        if (src[field] === undefined)
            continue;
        if (!isString(src[field]))
            return { ok: false, reason: `slackVoice.${field} must be a string` };
        slackVoice[field] = src[field];
    }
    return { ok: true, value: slackVoice };
}
/** Parse the `jira` sub-object of a settings update, or `undefined` when absent. */
function parseJiraUpdate(raw) {
    if (!('jira' in raw) || raw.jira === undefined)
        return { ok: true, value: undefined };
    if (!isRecord(raw.jira))
        return { ok: false, reason: 'jira settings must be an object' };
    const jira = {};
    const src = raw.jira;
    const stringFields = ['baseUrl', 'email', 'authMethod', 'oauthSiteName'];
    for (const field of stringFields) {
        if (src[field] === undefined)
            continue;
        if (!isString(src[field]))
            return { ok: false, reason: `jira.${field} must be a string` };
        jira[field] = src[field];
    }
    if (src.oauthConnected !== undefined) {
        if (!isBoolean(src.oauthConnected))
            return { ok: false, reason: 'jira.oauthConnected must be a boolean' };
        jira.oauthConnected = src.oauthConnected;
    }
    if (src.apiTokenExpiresAt !== undefined) {
        if (src.apiTokenExpiresAt !== null && !isString(src.apiTokenExpiresAt)) {
            return { ok: false, reason: 'jira.apiTokenExpiresAt must be an ISO date string or null' };
        }
        if (isString(src.apiTokenExpiresAt) && Number.isNaN(Date.parse(src.apiTokenExpiresAt))) {
            return { ok: false, reason: 'jira.apiTokenExpiresAt must be a valid date' };
        }
        jira.apiTokenExpiresAt = src.apiTokenExpiresAt;
    }
    return { ok: true, value: jira };
}
/** Parse the `competence` sub-object of a settings update, or `undefined` when absent. */
function parseCompetenceUpdate(raw) {
    if (!('competence' in raw) || raw.competence === undefined)
        return { ok: true, value: undefined };
    if (!isRecord(raw.competence))
        return { ok: false, reason: 'competence settings must be an object' };
    return { ok: true, value: normalizeCompetenceProfile(raw.competence) };
}
/** Parse the `gmail` sub-object of a settings update, or `undefined` when absent. */
function parseGmailUpdate(raw) {
    if (!('gmail' in raw) || raw.gmail === undefined)
        return { ok: true, value: undefined };
    if (!isRecord(raw.gmail))
        return { ok: false, reason: 'gmail settings must be an object' };
    if (raw.gmail.rules !== undefined && !isRecord(raw.gmail.rules)) {
        return { ok: false, reason: 'gmail.rules must be an object' };
    }
    return { ok: true, value: { rules: normalizeGmailAttentionRules(raw.gmail.rules) } };
}
const STRING_SECRET_FIELDS = [
    'slackUserToken',
    'opencodeGoApiKey',
    'geminiApiKey',
    'jiraApiToken',
    'githubPersonalAccessToken',
];
const CLEAR_FLAG_FIELDS = [
    'clearSlackUserToken',
    'clearOpencodeGoApiKey',
    'clearJiraApiToken',
    'clearGithubPersonalAccessToken',
    'clearGeminiApiKey',
    'clearOpenAiCodexOAuth',
];
/** Copy flat string-secret fields (when present) onto `result`, or report the first type error. */
function applyStringSecrets(raw, result) {
    for (const field of STRING_SECRET_FIELDS) {
        if (!(field in raw) || raw[field] === undefined)
            continue;
        if (!isString(raw[field]))
            return { ok: false, reason: `${field} must be a string` };
        result[field] = raw[field];
    }
    return { ok: true, value: true };
}
/** Copy flat boolean clear-flag fields (when present) onto `result`, or report the first type error. */
function applyClearFlags(raw, result) {
    for (const field of CLEAR_FLAG_FIELDS) {
        if (!(field in raw) || raw[field] === undefined)
            continue;
        if (!isBoolean(raw[field]))
            return { ok: false, reason: `${field} must be a boolean` };
        result[field] = raw[field];
    }
    return { ok: true, value: true };
}
export function validateUserSettingsUpdate(raw) {
    if (!isRecord(raw)) {
        return { ok: false, reason: 'Request body must be a JSON object' };
    }
    const result = {};
    const ai = parseAiUpdate(raw);
    if (!ai.ok)
        return ai;
    if (ai.value !== undefined)
        result.ai = ai.value;
    const slackVoice = parseSlackVoiceUpdate(raw);
    if (!slackVoice.ok)
        return slackVoice;
    if (slackVoice.value !== undefined)
        result.slackVoice = slackVoice.value;
    const jira = parseJiraUpdate(raw);
    if (!jira.ok)
        return jira;
    if (jira.value !== undefined)
        result.jira = jira.value;
    const gmail = parseGmailUpdate(raw);
    if (!gmail.ok)
        return gmail;
    if (gmail.value !== undefined)
        result.gmail = gmail.value;
    const competence = parseCompetenceUpdate(raw);
    if (!competence.ok)
        return competence;
    if (competence.value !== undefined)
        result.competence = competence.value;
    const secrets = applyStringSecrets(raw, result);
    if (!secrets.ok)
        return secrets;
    const clearFlags = applyClearFlags(raw, result);
    if (!clearFlags.ok)
        return clearFlags;
    return { ok: true, value: result };
}
//# sourceMappingURL=runtimeValidation.js.map